import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { useLocale } from '~/hooks/useLocale';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { type IncludeKey, type Timeframe } from './useStudentGenerateMessageMutation';
import { timeframeToApiParams } from './studentDataParams';

const log = createLogger('useStudentDataLogQuery');

export const STUDENT_DATA_LOG_QUERY_KEY = 'studentDataLog';

export type StudentDataLogEvent = {
  type: string;
  indication: string;
  context: string;
  timestamp: string;
  category?: string;
};

type StudentDataLogCounts = {
  warnings?: number;
  praises?: number;
  absences?: number;
};

type StudentDataLogResponse = {
  events: StudentDataLogEvent[];
  counts: StudentDataLogCounts;
  page: number;
  pageSize: number;
  totalCount: number;
};

const PAGE_SIZE = 50;

export function useStudentDataLogQuery({
  studentId,
  periodId,
  periodCreatedAt,
  timeframe,
  include,
  page = 1,
}: {
  studentId: string;
  periodId: string;
  periodCreatedAt: string;
  timeframe: Timeframe;
  include: IncludeKey[];
  page?: number;
}) {
  const httpClient = useHttpClient();
  const locale = useLocale();

  const { data, error, status } = useQuery<StudentDataLogResponse | null>({
    queryKey: [STUDENT_DATA_LOG_QUERY_KEY, studentId, periodId, timeframe, periodCreatedAt, include, page],
    queryFn: async () => {
      log.debug('Fetching student data log', { studentId, periodId, timeframe, page });

      const tz = locale?.timeZone ?? '';
      const dateParams = timeframeToApiParams(timeframe, periodCreatedAt);

      const body: Record<string, unknown> = { studentId, periodId, tz, ...dateParams };
      body.include = include;
      body.page = page;
      body.pageSize = PAGE_SIZE;

      const result = await httpClient.post<StudentDataLogResponse>('/report/getDataLog', body);
      log.info('Fetched student data log successfully', {
        studentId,
        eventCount: result.events.length,
        page,
      });

      // Filter present attendance events out of response since we are only showing praises, warnings, andabsences
      const filteredEvents = result.events.filter(
        (event) => event.type !== 'attendance' && event.indication !== 'present',
      );

      return { ...result, events: filteredEvents };
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch student data log', failureCount, error, log }),
    enabled: !!studentId && !!periodId,
    refetchOnWindowFocus: false,
  });

  const hasNextPage = data ? data.page * data.pageSize < data.totalCount : false;

  return { data, error, status, hasNextPage };
}
